/**
 * Claude Code Hook Protocol — parse stdin JSON input and emit hook response JSON.
 *
 * Implements the protocol defined at https://code.claude.com/docs/en/hooks
 * for SessionStart, UserPromptSubmit, and PreToolUse hook events.
 */

// ---------------------------------------------------------------------------
// Hook Event Types
// ---------------------------------------------------------------------------

export type HookEventName = "SessionStart" | "UserPromptSubmit" | "PreToolUse";

export interface SessionStartInput {
  hookEventName: "SessionStart";
  session_id?: string;
  type?: "new" | "resume" | "clear" | "compact";
}

export interface UserPromptSubmitInput {
  hookEventName: "UserPromptSubmit";
  session_id?: string;
  prompt?: string;
}

export interface PreToolUseInput {
  hookEventName: "PreToolUse";
  session_id?: string;
  tool_name?: string;
  tool_input?: {
    file_path?: string;
    path?: string;
    command?: string;
    content?: string;
    [key: string]: unknown;
  };
}

export type HookInput = SessionStartInput | UserPromptSubmitInput | PreToolUseInput;

// ---------------------------------------------------------------------------
// Hook Response Types
// ---------------------------------------------------------------------------

export type PermissionDecision = "allow" | "deny" | "ask";

export interface HookOutput {
  /** Hook-specific output envelope. */
  hookSpecificOutput?: {
    hookEventName: HookEventName;
    /** Additional context injected into the session. */
    additionalContext?: string;
    /** Permission decision for PreToolUse hooks. */
    permissionDecision?: PermissionDecision;
    /** Reason for the permission decision. */
    permissionDecisionReason?: string;
  };
}

// ---------------------------------------------------------------------------
// Parse / Emit
// ---------------------------------------------------------------------------

/**
 * Parse hook input from a raw JSON string (typically from stdin).
 * Throws on invalid JSON but not on missing fields — callers handle defaults.
 */
export function parseHookInput(raw: string): HookInput {
  const parsed = JSON.parse(raw) as HookInput;
  return parsed;
}

/**
 * Read all of stdin and parse as hook input JSON.
 */
export async function readHookInput(): Promise<HookInput> {
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk as string));
  }
  const raw = Buffer.concat(chunks).toString("utf-8").trim();
  if (!raw) {
    throw new Error("Empty stdin — no hook input received");
  }
  return parseHookInput(raw);
}

/**
 * Emit a hook response as JSON to stdout.
 */
export function emitHookOutput(output: HookOutput): void {
  process.stdout.write(JSON.stringify(output) + "\n");
}

// ---------------------------------------------------------------------------
// Response Builders
// ---------------------------------------------------------------------------

/**
 * Build a response that injects additional context into the session.
 */
export function buildContextResponse(
  eventName: HookEventName,
  additionalContext: string,
): HookOutput {
  return {
    hookSpecificOutput: {
      hookEventName: eventName,
      additionalContext,
    },
  };
}

/**
 * Build a deny response for PreToolUse hooks.
 */
export function buildDenyResponse(reason: string): HookOutput {
  return {
    hookSpecificOutput: {
      hookEventName: "PreToolUse",
      permissionDecision: "deny",
      permissionDecisionReason: reason,
    },
  };
}

/**
 * Build an allow response (empty output = allow by default).
 */
export function buildAllowResponse(): HookOutput {
  return {};
}
